import { type LucideIcon, Moon, Sun, SunMoon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { originOf, THEME_PREFS, type ThemePref, useTheme } from '../lib/theme';

const ICONS: Record<ThemePref, LucideIcon> = { light: Sun, dark: Moon, system: SunMoon };
const NEXT: Record<ThemePref, ThemePref> = { light: 'dark', dark: 'system', system: 'light' };

/** Light / dark / auto as a three-way switch (sidebar). */
export function ThemeSwitch() {
  const { t } = useTranslation();
  const { pref, setPref } = useTheme();
  return (
    <div
      role="radiogroup"
      aria-label={t('theme.label')}
      className="grid grid-cols-3 gap-0.5 rounded-lg bg-stone-100 p-0.5"
    >
      {THEME_PREFS.map((p) => {
        const Icon = ICONS[p];
        const on = pref === p;
        return (
          <button
            key={p}
            type="button"
            role="radio"
            aria-checked={on}
            title={p === 'system' ? t('theme.systemHint') : undefined}
            onClick={(e) => setPref(p, originOf(e.currentTarget))}
            className={`flex h-7 items-center justify-center gap-1.5 rounded-md text-xs font-medium transition-colors ${on ? 'bg-surface text-stone-900 shadow-xs ring-1 ring-stone-900/5' : 'text-stone-500 hover:text-stone-900'}`}
          >
            <Icon aria-hidden className={`size-3.5 ${on ? 'text-copper-600' : ''}`} />
            {t(`theme.${p}`)}
          </button>
        );
      })}
    </div>
  );
}

/** One button that steps light → dark → auto (collapsed rail, phone header, sign-in). */
export function ThemeCycleButton({ className }: { className: string }) {
  const { t } = useTranslation();
  const { pref, setPref } = useTheme();
  const Icon = ICONS[pref];
  const label = t('theme.cycle', { name: t(`theme.${pref}`) });
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={(e) => setPref(NEXT[pref], originOf(e.currentTarget))}
      className={className}
    >
      <Icon aria-hidden className="size-4" />
    </button>
  );
}
